import { useMemo, useState } from "react";
import { ArrowDown01Icon, Tag01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  Show
} from "shared/components";
import { Card } from "shared/components/Card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { AssigneeSearchInput } from "shared/components/Assignee/AssigneeSearchInput";
import { cx } from "shared/utils";
import { compareLeadTagNames } from "api/lead-tags";
import { LeadsFilters } from "shared/app/LeadsTableView/types";
import { useLeadsFilterOptions } from "shared/app/LeadsTableView/hooks/useLeadsFilterOptions";

type SelectedTag = { id: string; name: string };

const NOOP = () => {};

const getTriggerLabel = (selected: SelectedTag[]) => {
  if (!selected.length) return "All";
  const [first] = selected.toSorted((a, b) =>
    compareLeadTagNames(a.name, b.name)
  );
  return selected.length === 1
    ? first.name
    : `${first.name} +${selected.length - 1}`;
};

/**
 * "Tags: …" control beside Assigned to. Ticking a tag filters right away; several
 * tags show leads carrying any of them. Counts follow the other applied filters.
 */
export const TagsFilterPopover = ({
  open,
  onOpenChange,
  filters,
  onSelectedTagsChange,
  propertyIds,
  unitIds,
  search
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters?: LeadsFilters;
  onSelectedTagsChange: (tags: SelectedTag[]) => void;
  propertyIds?: string[];
  unitIds?: string[];
  search?: string;
}) => {
  const [query, setQuery] = useState("");
  const selected = useMemo(() => filters?.tags ?? [], [filters?.tags]);
  const selectedIds = useMemo(
    () => new Set(selected.map((tag) => tag.id)),
    [selected]
  );

  const { data: filterOptions, isLoading } = useLeadsFilterOptions({
    filters,
    propertyIds,
    unitIds,
    search,
    enabled: open
  });

  const tags = useMemo(() => filterOptions?.tags ?? [], [filterOptions?.tags]);
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle
      ? tags.filter((tag) => tag.name.toLowerCase().includes(needle))
      : tags;
  }, [tags, query]);

  const toggleTag = (tag: { id: number; name: string }) => {
    const id = String(tag.id);
    onSelectedTagsChange(
      selectedIds.has(id)
        ? selected.filter((item) => item.id !== id)
        : [...selected, { id, name: tag.name }]
    );
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange} placement="bottom-start">
      <PopoverTrigger onClick={() => onOpenChange(true)}>
        <Card className="w-fit max-w-[314px] px-4 py-2 flex items-center gap-2 min-h-[40px] h-full shadow-xs cursor-pointer">
          <HugeiconsIcon
            icon={Tag01Icon}
            size={20}
            className="text-content-200 shrink-0"
          />
          <span className="text-content-200 font-bold text-xs shrink-0">
            Tags:
          </span>
          <span className="font-bold text-content-1000 text-xs truncate">
            {getTriggerLabel(selected)}
          </span>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={16}
            className={cx(
              "text-content-1000 shrink-0",
              open ? "rotate-180" : ""
            )}
          />
        </Card>
      </PopoverTrigger>
      <PopoverContent className="max-w-[314px] w-full p-0 rounded-lg border border-alpha-100 shadow-lg bg-content-1000-inverted z-10">
        <div className="flex w-full items-center justify-between gap-3 px-4 py-3 border-b border-alpha-40">
          <span className="text-sm font-bold text-content-1000">Tags</span>
          <Button
            type="button"
            variant={ButtonVariant.TEXT}
            size={ButtonSize.SM}
            disabled={!selected.length}
            onClick={() => onSelectedTagsChange([])}
          >
            Clear
          </Button>
        </div>
        <AssigneeSearchInput
          id="tags-filter-search"
          placeholder="Search tags"
          value={query}
          onChange={setQuery}
        />
        <div className="max-h-[300px] overflow-y-auto py-1">
          <Show if={isLoading}>
            <p className="px-4 py-2.5 text-sm text-content-400">
              Loading tags...
            </p>
          </Show>
          <Show if={!isLoading && tags.length === 0}>
            <p className="px-4 py-2.5 text-sm text-content-400">
              No tags yet. Add a tag to a lead and it will show up here.
            </p>
          </Show>
          <Show if={tags.length > 0 && matches.length === 0}>
            <p className="px-4 py-2.5 text-sm text-content-400">
              No matching tags
            </p>
          </Show>
          {matches.map((tag) => (
            <div
              key={tag.id}
              role="option"
              aria-selected={selectedIds.has(String(tag.id))}
              className="flex w-full items-center gap-3 px-4 py-2.5 hover:bg-alpha-40 cursor-pointer"
              onClick={() => toggleTag(tag)}
            >
              <Checkbox
                checked={selectedIds.has(String(tag.id))}
                size={CheckboxSize.SM}
                wrapperClassName="!w-auto !flex-none !p-0 justify-center"
                onChange={NOOP}
              />
              <span className="truncate text-sm font-medium text-content-1000">
                {tag.name}
              </span>
              <span className="ml-auto text-xs text-content-300">
                {tag.count}
              </span>
            </div>
          ))}
        </div>
        <p className="border-t border-alpha-40 bg-alpha-20 px-4 py-2 text-xs text-content-400">
          Shows leads with any selected tag
        </p>
      </PopoverContent>
    </Popover>
  );
};
